/**
 * Video Overlay Modal
 * Handles opening, closing, focus management, and video playback control
 */

class VideoOverlayModal {
  constructor(modalElement) {
    this.modal = modalElement;
    this.modalId = modalElement.id;
    
    // Move modal to body if it's not already there
    if (this.modal.parentElement !== document.body) {
      console.log('Moving modal to body:', this.modalId);
      document.body.appendChild(this.modal);
    }
    
    this.trigger = document.querySelector(`[data-video-trigger="${this.modalId}"]`);
    this.backdrop = modalElement.querySelector('[data-modal-backdrop]');
    this.closeButton = modalElement.querySelector('[data-modal-close]');
    this.videoContainer = modalElement.querySelector('[data-video-container]');
    this.videoIframe = modalElement.querySelector('[data-video-iframe]');
    this.videoElement = modalElement.querySelector('[data-video-element]');
    this.container = modalElement.querySelector('.video-overlay-modal__container');
    
    // Get settings from data attributes
    this.shouldAutoplay = modalElement.dataset.autoplay === 'true';
    this.shouldBeMuted = modalElement.dataset.muted === 'true';
    
    this.isOpen = false;
    this.focusableElements = [];
    this.firstFocusable = null;
    this.lastFocusable = null;
    this.previousActiveElement = null;
    this.iframeSrc = null;
    
    // Store iframe data-src for lazy loading
    if (this.videoIframe) {
      this.iframeSrc = this.videoIframe.dataset.src;
    }
    
    this.init();
  }
  
  init() {
    if (!this.trigger) {
      console.log('No trigger found for modal:', this.modalId);
      return;
    }
    
    // Bind methods to preserve 'this' context
    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.trapFocus = this.trapFocus.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);
    
    // Bind events
    this.trigger.addEventListener('click', this.handleOpen);
    this.closeButton.addEventListener('click', this.handleClose);
    
    // Click anywhere in modal to close (except video container)
    this.modal.addEventListener('click', this.handleModalClick);
    
    console.log('VideoOverlayModal initialized:', this.modalId);
  }
  
  handleModalClick(e) {
    // Close if clicking outside the video container
    if (!this.container.contains(e.target)) {
      console.log('Clicked outside container, closing modal');
      this.close();
    }
  }
  
  handleOpen(e) {
    console.log('Opening modal:', this.modalId);
    this.open(e);
  }
  
  handleClose(e) {
    if (e) e.preventDefault();
    console.log('Closing modal:', this.modalId);
    this.close();
  }
  
  open(clickEvent) {
    // Show modal
    this.modal.removeAttribute('hidden');
    this.modal.style.display = '';
    this.isOpen = true;
    this.previousActiveElement = document.activeElement;
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Load iframe src if using lazy loading
    if (this.videoIframe && this.iframeSrc) {
      console.log('Loading iframe src:', this.iframeSrc);
      this.videoIframe.src = this.iframeSrc;
    }
    
    // Play HTML5 video
    if (this.videoElement && this.shouldAutoplay) {
      // Set muted state based on settings
      this.videoElement.muted = this.shouldBeMuted;
      
      console.log('Playing video, muted:', this.shouldBeMuted);
      
      this.videoElement.play()
        .then(() => {
          console.log('Video playing successfully');
        })
        .catch((error) => {
          console.log('Autoplay blocked, trying muted:', error.message);
          this.videoElement.muted = true;
          return this.videoElement.play();
        })
        .catch((error) => {
          console.log('All autoplay attempts failed:', error);
        });
    }
    
    // Setup focus trap
    this.setupFocusTrap();
    
    // Focus close button
    requestAnimationFrame(() => {
      this.closeButton.focus();
    });
    
    // Add event listeners
    document.addEventListener('keydown', this.handleKeydown);
  }
  
  close() {
    if (!this.isOpen) return;
    
    console.log('Closing modal, stopping video');
    
    // Hide modal
    this.modal.setAttribute('hidden', '');
    this.modal.style.display = 'none';
    this.isOpen = false;
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Stop video playback
    this.stopVideo();
    
    // Remove event listeners
    document.removeEventListener('keydown', this.handleKeydown);
    
    // Restore focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
  }
  
  stopVideo() {
    // Stop iframe video by clearing src
    if (this.videoIframe) {
      console.log('Stopping iframe video');
      this.videoIframe.src = 'about:blank';
    }
    
    // Pause HTML5 video
    if (this.videoElement) {
      console.log('Pausing HTML5 video');
      this.videoElement.pause();
      this.videoElement.currentTime = 0;
    }
  }
  
  setupFocusTrap() {
    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    this.focusableElements = Array.from(this.modal.querySelectorAll(focusableSelectors))
      .filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
    
    this.firstFocusable = this.focusableElements[0];
    this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
  }
  
  trapFocus(e) {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusable) {
        e.preventDefault();
        this.lastFocusable.focus();
      }
    } else {
      if (document.activeElement === this.lastFocusable) {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    }
  }
  
  handleKeydown(e) {
    if (e.key === 'Escape') {
      console.log('ESC pressed, closing modal');
      this.close();
    } else if (e.key === 'Tab') {
      this.trapFocus(e);
    }
  }
}

// Initialize all video modals on page
function initVideoOverlays() {
  console.log('Initializing video overlays...');
  const modals = document.querySelectorAll('[data-video-modal]');
  console.log('Found', modals.length, 'video modals');
  modals.forEach(modal => {
    // Skip if already initialized
    if (modal.dataset.initialized) return;
    modal.dataset.initialized = 'true';
    new VideoOverlayModal(modal);
  });
}

// Run on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoOverlays);
} else {
  initVideoOverlays();
}

// Also run when Shopify section is rendered (for theme editor)
document.addEventListener('shopify:section:load', function() {
  console.log('Section loaded, reinitializing video overlays');
  initVideoOverlays();
});

// Export for manual initialization if needed
window.VideoOverlayModal = VideoOverlayModal;
window.initVideoOverlays = initVideoOverlays;